"use client"

import * as React from "react"

import { cn } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { ModuleAccessLevel } from "@/features/users/model/team-panel"
import type { CombinedModuleAccessRow } from "@/features/users/lib/module-access"
import { ProgressRowPill, ProgressRowPillTone } from "@/shared/components/progress-rows"

/** Modifier teal, Voir blue, Aucun muted (design I1). */
const ACCESS_TONE: Record<ModuleAccessLevel, ProgressRowPillTone> = {
  [ModuleAccessLevel.Edit]: ProgressRowPillTone.Success,
  [ModuleAccessLevel.View]: ProgressRowPillTone.Info,
  [ModuleAccessLevel.None]: ProgressRowPillTone.Neutral,
}

const LEVEL_ORDER: readonly ModuleAccessLevel[] = [ModuleAccessLevel.None, ModuleAccessLevel.View, ModuleAccessLevel.Edit]

/** "Esther Uwase" reads "EU". */
function nameInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? "") + (parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "")).toUpperCase()
}

/** "esther.uwase@gmail.com" reads "EU": the first letters of the address's name parts. */
export function emailInitials(email: string): string {
  const local = email.trim().split("@")[0] ?? ""
  const parts = local.split(/[._\-+\s]+/).filter((part) => /[a-z0-9]/i.test(part))
  const letters = parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : local.replace(/[^a-z0-9]/gi, "").slice(0, 2)
  return letters.toUpperCase()
}

/**
 * The right-hand panel of the invite dialog ("Ce qu'elle pourra faire"): who is invited and where, then each
 * module at Aucun / Voir / Modifier, worked out from the roles ticked so far. A summary for reading; the API
 * evaluates the same roles on every request.
 */
export function InviteAccessPreview({
  email,
  name,
  scopeSummary,
  rows,
  baseline,
  note,
  className,
}: {
  email: string
  /** Editing a person: their name, shown above the address. */
  name?: string
  scopeSummary: string
  rows: readonly CombinedModuleAccessRow[]
  /** Editing: the same modules as they are today, so the panel can say what changes. */
  baseline?: readonly CombinedModuleAccessRow[]
  note: string
  className?: string
}) {
  const t = useTranslations("users.inviteModal.overlay")
  const tAccess = useTranslations("users.panel")
  const tGrid = useTranslations("roles.permissionGrid")
  const address = email.trim()
  const levelLabel = (level: ModuleAccessLevel) => tAccess(`access.${level}` as never)
  const changes = baseline
    ? rows.flatMap((row) => {
        const before = baseline.find((entry) => entry.module === row.module)?.level ?? ModuleAccessLevel.None
        if (before === row.level) return []
        const moduleLabel = tGrid(`modules.${row.module}` as never)
        const up = LEVEL_ORDER.indexOf(row.level) > LEVEL_ORDER.indexOf(before)
        const text =
          row.level === ModuleAccessLevel.None
            ? t("changeRemoved", { module: moduleLabel })
            : t(up ? "changeUp" : "changeDown", { module: moduleLabel, level: levelLabel(row.level) })
        return [{ module: row.module, up, text }]
      })
    : null

  return (
    <div className={cn("flex flex-col gap-3", className)} data-testid='invite-access-preview'>
      <div className='bg-card border-border/50 flex items-center gap-2.5 rounded-2xl border p-3.5'>
        <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sm font-extrabold text-sky-800 dark:bg-sky-500/20 dark:text-sky-200'>
          {name ? nameInitials(name) : address ? emailInitials(address) : "?"}
        </span>
        <div className='min-w-0'>
          <p className='text-foreground truncate text-sm font-bold'>{name || address || t("newPerson")}</p>
          {name && address ? <p className='text-muted-foreground truncate text-[12.5px]'>{address}</p> : null}
          <p className='text-muted-foreground truncate text-[12.5px]' data-testid='invite-scope-summary'>
            {scopeSummary}
          </p>
        </div>
      </div>
      <div className='bg-card border-border/50 divide-border/50 divide-y rounded-2xl border px-3.5 py-1'>
        {rows.map((row) => (
          <div key={row.module} className='flex items-center justify-between gap-2 py-[7px] text-[13px]' data-testid={`invite-access-${row.module}`}>
            <span className='min-w-0 truncate'>
              {tGrid(`modules.${row.module}` as never)}
              {row.varies ? <span className='text-muted-foreground ml-1.5 text-[11.5px]'>{t("variesByBranch")}</span> : null}
            </span>
            <ProgressRowPill tone={ACCESS_TONE[row.level]}>{levelLabel(row.level)}</ProgressRowPill>
          </div>
        ))}
      </div>
      {changes ? (
        <div className='bg-card border-border/50 rounded-2xl border px-3.5 py-2.5' data-testid='invite-access-changes'>
          <p className='text-muted-foreground mb-1 text-xs font-extrabold'>{t("changesTitle")}</p>
          {changes.length === 0 ? (
            <p className='text-muted-foreground text-[13px]'>{t("noChanges")}</p>
          ) : (
            <ul className='flex flex-col gap-0.5 text-[13px] font-semibold'>
              {changes.map((change) => (
                <li key={change.module} className={change.up ? "text-teal-800 dark:text-teal-300" : "text-destructive"}>
                  {change.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      <p className='text-muted-foreground text-xs leading-[17px]'>{note}</p>
    </div>
  )
}
